import * as Yup from 'yup'
import { TaskMutationDto, TaskOverviewDto } from '../../config/taskConfig'
import { getTaskDateLabelKey, isBelowMinimumDuration, ResolvedTaskDateConfig } from '../../helpers/taskDateHelper'

/**
 * Maps an item from the task overview onto the mutation DTO the add/edit dialog works with (HAM-4275).
 * The overview carries references (file, assignee, assigner) where the mutation wants plain ids.
 */
export function mapTaskOverviewDtoToMutation(item: TaskOverviewDto): TaskMutationDto {
    return {
        category: item.category,
        status: item.status,
        highlight: item.highlight,
        today: item.today,
        week: item.week,
        title: item.title,
        description: item.description,
        start: item.start,
        due: item.due,
        duration: item.duration,
        fileId: item.file?.id,
        assigneeId: item.assignee?.id,
        assignerId: item.assigner?.id,
        attachmentIds: item.attachmentIds || [],
        workflowStepName: item.workflowStepName,
    }
}

/**
 * Validation for the task dialog. A factory because the messages are translated; `hasCategories` reflects whether the
 * tenant configured task categories at all, because the field is only shown - and only required - when it did, and
 * `dateConfig` carries the date handling of the office (HAM-4286): which dates exist and which of them are required.
 */
export function getTaskValidationSchema(
    translate: (key: string, options?: Record<string, unknown>) => string,
    hasCategories: boolean,
    dateConfig: ResolvedTaskDateConfig,
): Yup.ObjectSchema<any> {
    // With a single date the field is simply labelled "Datum", whichever of the two dates the office kept.
    const startLabel = translate(getTaskDateLabelKey('start', dateConfig))
    const dueLabel = translate(getTaskDateLabelKey('due', dateConfig))

    return Yup.object().shape({
        category: hasCategories
            ? Yup.string().label(translate('task.category.label')).required()
            : Yup.string().label(translate('task.category.label')).nullable(),
        title: Yup.string().required().label(translate('task.title')),
        description: Yup.string().nullable().label(translate('task.description')),
        start: (dateConfig.start.required ? Yup.string().required() : Yup.string().notRequired()).label(startLabel),
        due: (dateConfig.due.required ? Yup.string().required() : Yup.string().notRequired())
            .label(dueLabel)
            // A vestige: the dates move along with each other while editing, so they can no longer end up out of order.
            .test('is-valid-date-range', translate('task.edit.form.due.error.daterange'), (value, context) => {
                return !value || !context.parent.start || new Date(value) >= new Date(context.parent.start)
            }),
        duration: Yup.string()
            .notRequired()
            .label(translate('task.duration'))
            .test(
                'is-above-minimum',
                translate('task.edit.form.duration.error.minimum', { minimum: dateConfig.duration.minimum }),
                value => !isBelowMinimumDuration(value, dateConfig.duration.minimum),
            ),
        assigneeId: Yup.string().uuid().required().label(translate('task.assigneeid')),
        assignerId: Yup.string().uuid().required().label(translate('task.assignerid')),
        fileId: Yup.string().uuid().required().label(translate('base.filepicker.label')),
        attachmentIds: Yup.array(),
        status: Yup.string().required().label(translate('task.status.label')),
    })
}
